import { lightTheme, darkTheme } from './defaultTheme';

function isObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/** Deep-merge plain objects (arrays / primitives replaced). */
export function deepMerge(base, override) {
  if (!isObject(override)) return base;
  const result = { ...base };
  Object.keys(override).forEach((key) => {
    const next = override[key];
    if (isObject(next) && isObject(base[key])) {
      result[key] = deepMerge(base[key], next);
    } else if (next !== undefined) {
      result[key] = next;
    }
  });
  return result;
}

/**
 * Normalize parent `theme` prop into { light, dark } packs.
 *
 * Supported shapes:
 * 1. Full theme file: `{ light: {...}, dark: {...} }`
 * 2. Flat tokens: `{ colors, radii, ... }` → applied to both modes (merged over defaults)
 * 3. Mode-specific partial: `{ light: { colors: { primary } } }`
 */
export function normalizeThemeFile(themeProp) {
  if (!themeProp || !isObject(themeProp)) {
    return { light: lightTheme, dark: darkTheme };
  }

  const hasModes = isObject(themeProp.light) || isObject(themeProp.dark);

  if (hasModes) {
    return {
      light: deepMerge(lightTheme, themeProp.light || {}),
      dark: deepMerge(darkTheme, themeProp.dark || {}),
    };
  }

  // Flat token object — treat as overrides for both modes
  return {
    light: deepMerge(lightTheme, themeProp),
    dark: deepMerge(darkTheme, themeProp),
  };
}

/**
 * Pick active theme for current mode and ensure mode flag is set.
 */
export function resolveActiveTheme(themeFile, darkMode = false) {
  const pack = normalizeThemeFile(themeFile);
  const active = darkMode ? pack.dark : pack.light;
  return {
    ...active,
    mode: darkMode ? 'dark' : 'light',
    __pack: pack,
  };
}

/** camelCase / nested keys → CSS custom property map for .ca-root */
const COLOR_VAR_MAP = {
  primary: '--ca-primary',
  primaryHover: '--ca-primary-hover',
  text: '--ca-text',
  textSecondary: '--ca-text-secondary',
  textMuted: '--ca-text-muted',
  border: '--ca-border',
  borderLight: '--ca-border-light',
  background: '--ca-bg',
  surface: '--ca-surface',
  white: '--ca-white',
  onPrimary: '--ca-on-primary',
  activeBg: '--ca-active-bg',
  activeBorder: '--ca-active-border',
  activeText: '--ca-active-text',
  inactiveBg: '--ca-inactive-bg',
  inactiveBorder: '--ca-inactive-border',
  inactiveText: '--ca-inactive-text',
  link: '--ca-link',
  divider: '--ca-divider',
  accentBorder: '--ca-accent-border',
  accentBg: '--ca-accent-bg',
  stepDone: '--ca-step-done',
  success: '--ca-success',
  danger: '--ca-danger',
  secondaryBtn: '--ca-secondary-btn',
  secondaryBtnHover: '--ca-secondary-btn-hover',
  inputBg: '--ca-input-bg',
  tableHeadBg: '--ca-table-head-bg',
  sidebarBg: '--ca-sidebar-bg',
  overlay: '--ca-overlay',
};

const RADIUS_VAR_MAP = {
  sm: '--ca-radius-sm',
  md: '--ca-radius-md',
  lg: '--ca-radius-lg',
  xl: '--ca-radius-xl',
  pill: '--ca-radius-pill',
};

const TYPE_VAR_MAP = {
  fontFamily: '--ca-font',
  h1Size: '--ca-h1-size',
  h2Size: '--ca-h2-size',
  bodySize: '--ca-body-size',
  labelSize: '--ca-label-size',
  valueSize: '--ca-value-size',
  smallSize: '--ca-small-size',
};

const SHADOW_VAR_MAP = {
  card: '--ca-shadow-card',
  settings: '--ca-shadow-settings',
};

/**
 * Convert a resolved theme object into inline CSS variable styles.
 */
export function themeToCssVars(theme) {
  const vars = {};

  if (theme?.colors) {
    Object.entries(COLOR_VAR_MAP).forEach(([key, cssVar]) => {
      if (theme.colors[key] != null) vars[cssVar] = theme.colors[key];
    });
  }

  if (theme?.radii) {
    Object.entries(RADIUS_VAR_MAP).forEach(([key, cssVar]) => {
      if (theme.radii[key] != null) vars[cssVar] = theme.radii[key];
    });
  }

  if (theme?.typography) {
    Object.entries(TYPE_VAR_MAP).forEach(([key, cssVar]) => {
      if (theme.typography[key] != null) vars[cssVar] = theme.typography[key];
    });
  }

  if (theme?.shadow) {
    Object.entries(SHADOW_VAR_MAP).forEach(([key, cssVar]) => {
      if (theme.shadow[key] != null) vars[cssVar] = theme.shadow[key];
    });
  }

  // Ensure primary button text stays readable
  if (vars['--ca-on-primary'] && !vars['--ca-white']) {
    vars['--ca-white'] = vars['--ca-on-primary'];
  }

  return vars;
}
